import {
    faCalculator,
    faPlug,
    faScrewdriverWrench
} from '@fortawesome/free-solid-svg-icons';
import {
    faCameraCctv,
    faSquareKanban,
    faCameraSlash
} from '@fortawesome/pro-solid-svg-icons';
import { createStackNavigator } from '@react-navigation/stack';
import AlarmCenterDetails from '../pages/crm/AlarmCenterDetails';
import AlarmCentersList from '../pages/crm/AlarmCentersList';
import CameraHistory from '../pages/crm/CameraHistory';
import MaintenanceRequests from '../pages/crm/MaintenanceRequests/MaintenanceRequests';
import Management from '../pages/crm/Management/Management';
import ManagementDetails from '../pages/crm/ManagementDetails/ManagementDetails';
import OfflineEventsKanban from '../pages/crm/OfflineEventsKanban';
import TicketReasons from '../pages/crm/TicketReason/TicketReasons';
import CompaniesCameraConfiguration from '../pages/crm/CompaniesCameraConfiguration/CompaniesCameraConfiguration';
import CompanyCameraConfigurationDetails from '../pages/crm/CompaniesCameraConfiguration/CompanyCameraConfigurationDetails';
import { Module } from '../Routes';
import { CrmSchemas } from '../schemas/crm';
import { CRMParamList } from '../typings/Params';
import TicketDetail from '../pages/crm/TicketDetail/TicketDetail';
import KanbanTicketDetail from '../pages/crm/TicketDetail/KanbanTicketDetail';
import TicketCameraDetail from '../pages/crm/TicketDetail/TicketCameraDetail';
import KanbanMaintenanceRequests from '../pages/crm/KanbanMaintenanceRequests/KanbanMaintenanceRequests';
import MaintenanceRequestDetail from '../pages/crm/MaintenanceRequests/MaintenanceRequestDetail';
import KanbanMaintenanceRequestDetail from '../pages/crm/KanbanMaintenanceRequests/KanbanMaintenanceRequestDetail';

export const crmModule: Module<'CRM'> = {
    path: '/CRM',
    stackNavigator: createStackNavigator<CRMParamList>(),
    canAccess: (user, currentApp) => Boolean(
        !currentApp.isAgentApp && (
            user?.isAdmin
            || user?.permissions.crm_module_access
        )
    ),
    icon: require('../../assets/icon_crm.png'),
    logo: require('../../assets/logo_crm.png'),
    label: 'crm',
    navigate: (navigation) => navigation.navigate('CRM'),
    schemas: CrmSchemas,
    screens: {
        Management: {
            path: 'cameras',
            selectedItem: 'cameras',
            component: Management,
            label: 'cameras',
            canAccess: () => true,
            accessInformation: {
                icon: faCameraCctv,
                navigate: (navigation) => navigation.navigate('CRM', { screen: 'Management' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        ManagementDetails: {
            path: 'cameras/details/:id?',
            selectedItem: 'cameras',
            component: ManagementDetails,
            label: 'cameras',
            canAccess: () => true,
        },
        CameraHistory: {
            path: 'cameras/history/:id',
            selectedItem: 'cameras',
            component: CameraHistory,
            label: 'cameraHistory',
            canAccess: () => true,
            initialParams: () => ({
                page: 1,
                limit: 25,
                orderBy: 'start',
                orderDirection: 'asc',
                onlyOffline: false,
                onlyClosed: false,
            }),
        },
        AlarmCentersList: {
            path: 'alarm-centers',
            selectedItem: 'alarm-centers',
            component: AlarmCentersList,
            label: 'alarmCenters',
            canAccess: () => true,
            accessInformation: {
                icon: faCalculator,
                navigate: (navigation) => navigation.navigate('CRM', { screen: 'AlarmCentersList' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        AlarmCenterDetails: {
            path: 'alarm-centers/details/:id?',
            selectedItem: 'alarm-centers',
            component: AlarmCenterDetails,
            label: 'alarmCenters',
            canAccess: () => true,
        },
        OfflineEvents: {
            path: 'cameras/history',
            selectedItem: 'offline-events',
            component: CameraHistory,
            label: 'offlineEvents',
            canAccess: () => true,
            accessInformation: {
                icon: faPlug,
                navigate: (navigation) => navigation.navigate('CRM', { screen: 'OfflineEvents' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
                orderBy: 'start',
                orderDirection: 'asc',
                onlyOffline: false,
                onlyClosed: false,
            }),
        },
        OfflineEventsKanban: {
            path: 'offline-events-kanban',
            selectedItem: 'offline-events-kanban',
            component: OfflineEventsKanban,
            label: 'offlineEventsKanban',
            canAccess: (user, currentApp) => currentApp.isParentApp,
            accessInformation: {
                icon: faSquareKanban,
                iconModifier: faPlug,
                navigate: (navigation) => navigation.navigate('CRM', { screen: 'OfflineEventsKanban' }),
            },
        },
        KanbanTicketDetail: {
            path: 'offline-events-kanban/:id',
            selectedItem: 'offline-events-kanban',
            component: KanbanTicketDetail,
            label: 'offlineEventsKanban',
            canAccess: (user, currentApp) => currentApp.isParentApp,
        },
        TicketCameraDetail: {
            path: 'offline-ticket-camera/:id',
            selectedItem: 'cameras',
            component: TicketCameraDetail,
            label: 'offlineEventsKanban',
            canAccess: () => true,
        },
        TicketDetail: {
            path: 'offline-ticket/:id',
            selectedItem: 'offline-events',
            component: TicketDetail,
            label: 'offlineEventsKanban',
            canAccess: () => true,
        },
        MaintenanceRequests: {
            path: 'maintenance-requests',
            selectedItem: 'maintenance-requests',
            component: MaintenanceRequests,
            label: 'maintenanceRequests',
            canAccess: () => true,
            accessInformation: {
                icon: faScrewdriverWrench,
                navigate: (navigation) => navigation.navigate('CRM', { screen: 'MaintenanceRequests' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
                orderBy: 'start',
                orderDirection: 'desc',
                onlyClosed: false,
                onlyOffline: false,
            }),
        },
        MaintenanceRequestDetail: {
            path: 'maintenance-request/:id',
            selectedItem: 'maintenance-requests',
            component: MaintenanceRequestDetail,
            label: 'maintenanceRequestDetail',
            canAccess: () => true,
        },
        KanbanMaintenanceRequests: {
            path: 'kanban-maintenance-requests',
            selectedItem: 'kanban-maintenance-requests',
            component: KanbanMaintenanceRequests,
            label: 'kanbanMaintenanceRequests',
            canAccess: (user, currentApp) => currentApp.isParentApp,
            accessInformation: {
                icon: faSquareKanban,
                iconModifier: faScrewdriverWrench,
                navigate: (navigation) => navigation.navigate('CRM', { screen: 'KanbanMaintenanceRequests' }),
            },
        },
        KanbanMaintenanceRequestDetail: {
            path: 'kanban-maintenance-request/:id',
            selectedItem: 'kanban-maintenance-requests',
            component: KanbanMaintenanceRequestDetail,
            label: 'maintenanceRequestDetail',
            canAccess: (user, currentApp) => currentApp.isParentApp,
        },
        TicketReasons: {
            path: 'ticket-reasons',
            selectedItem: 'ticket-reasons',
            component: TicketReasons,
            label: 'drpScenarios',
            canAccess: () => true,
            accessInformation: {
                icon: faCameraSlash,
                navigate: (navigation) => navigation.navigate('CRM', { screen: 'TicketReasons' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        CompaniesCameraConfiguration: {
            path: 'companies-configuration',
            selectedItem: 'companies-configuration',
            component: CompaniesCameraConfiguration,
            label: 'companiesConfiguration',
            canAccess: (user, currentApp) => currentApp.isParentApp,
            accessInformation: {
                icon: faScrewdriverWrench,
                navigate: (navigation) => navigation.navigate('CRM', { screen: 'CompaniesCameraConfiguration' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        CompanyCameraConfigurationDetails: {
            path: 'companies-configuration/:companyId',
            selectedItem: 'companies-configuration',
            component: CompanyCameraConfigurationDetails,
            label: 'companiesConfiguration',
            canAccess: (user, currentApp) => currentApp.isParentApp,
        },
    }
};
